// availability-helpers.js — pure functions for resolving availability entries against dates.
//
// Resolution rules (CAL-LAST-WINS, 2026-05-17):
//   1. If any one-off entry covers the date, the most-recently-created one-off wins.
//      (Last action overrides previous one-offs — e.g. clicking "Available" after
//      previously clicking "Unavailable" makes the day available, no matter the order
//      in which the entries appear.)
//   2. Otherwise, among recurring entries (weekly + every-n-weeks) covering the date,
//      "unavailable wins" — the safer default for recurring patterns.
//   3. If nothing covers it, status is 'off' (which the responder UI interprets as
//      "implicitly available" in opt-out mode).

export function toJsDate(maybeTimestamp) {
  if (!maybeTimestamp) return null;
  if (maybeTimestamp instanceof Date) return maybeTimestamp;
  if (typeof maybeTimestamp.toDate === 'function') return maybeTimestamp.toDate();
  if (typeof maybeTimestamp.seconds === 'number') return new Date(maybeTimestamp.seconds * 1000);
  if (typeof maybeTimestamp === 'number') return new Date(maybeTimestamp);
  if (typeof maybeTimestamp === 'string') {
    const d = new Date(maybeTimestamp);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function ymdInTimezone(date, timezone) {
  if (!date) return null;
  const tz = timezone || undefined;
  try {
    return new Intl.DateTimeFormat('sv-SE', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat('sv-SE', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(date);
  }
}

export function dowInTimezone(date, timezone) {
  if (!date) return null;
  const tz = timezone || undefined;
  const short = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(date);
  const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return map[short] ?? null;
}

export function generateNextNDays(n, fromDate = new Date()) {
  const out = [];
  const start = new Date(fromDate);
  start.setHours(0, 0, 0, 0);
  for (let i = 0; i < n; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    out.push(d);
  }
  return out;
}

export function entryCoversDate(entry, dateYMD, timezone) {
  if (!entry || !dateYMD) return false;

  const startDate = toJsDate(entry.startDate);
  const endDate = toJsDate(entry.endDate);
  if (!startDate) return false;

  const startYMD = ymdInTimezone(startDate, timezone);
  if (dateYMD < startYMD) return false;

  if (endDate) {
    const endYMD = ymdInTimezone(endDate, timezone);
    if (dateYMD > endYMD) return false;
  }

  if (entry.type === 'one-off') {
    // CAL-BUG fix 2026-05-17: a one-off with no endDate is a SINGLE day.
    if (!endDate) return dateYMD === startYMD;
    return true;
  }

  const targetDate = ymdToDate(dateYMD);
  const targetDow = dowInTimezone(targetDate, timezone);
  if (!Array.isArray(entry.daysOfWeek) || !entry.daysOfWeek.includes(targetDow)) {
    return false;
  }

  if (entry.type === 'weekly') return true;

  if (entry.type === 'every-n-weeks') {
    const interval = Number(entry.weekInterval);
    if (!interval || interval < 1) return false;
    const startWeekStart = startOfWeek(ymdToDate(startYMD));
    const targetWeekStart = startOfWeek(targetDate);
    const weeksElapsed = Math.round(
      (targetWeekStart.getTime() - startWeekStart.getTime()) / (7 * 24 * 60 * 60 * 1000),
    );
    return weeksElapsed >= 0 && weeksElapsed % interval === 0;
  }

  return false;
}

/**
 * Get the effective status for a date, applying the CAL-LAST-WINS rules.
 */
export function getStatusForDate(entries, dateYMD, timezone) {
  if (!Array.isArray(entries) || entries.length === 0) return 'off';

  // Pass 1 — one-offs: most-recently-created wins.
  let winningOneOff = null;
  let winningTime = -1;
  for (const entry of entries) {
    if (entry.type !== 'one-off') continue;
    if (!entryCoversDate(entry, dateYMD, timezone)) continue;
    const t = createdAtMs(entry);
    if (t >= winningTime) {
      winningOneOff = entry;
      winningTime = t;
    }
  }
  if (winningOneOff) return winningOneOff.status;

  // Pass 2 — recurring rules: CAL-RECUR-WIPE (Task #95, 2026-05-20) flipped this
  // from "unavailable wins" to "most-recent-created wins", matching Pass 1.
  // Why: toggling between "every other Fri Unavailable" and "every other Fri
  // Available" via the popover used to stack BOTH rules; the unavailable-wins
  // tie-breaker meant a flip-to-available looked broken. Most-recent-wins is
  // less safe in the abstract but matches what the responder actually clicked
  // last, which is the only thing they remember.
  let winningRecurring = null;
  let winningRecurringTime = -1;
  for (const entry of entries) {
    if (entry.type === 'one-off') continue;
    if (!entryCoversDate(entry, dateYMD, timezone)) continue;
    const t = createdAtMs(entry);
    if (t >= winningRecurringTime) {
      winningRecurring = entry;
      winningRecurringTime = t;
    }
  }
  return winningRecurring ? winningRecurring.status : 'off';
}

function createdAtMs(entry) {
  const c = entry.createdAt;
  if (!c) return 0;
  if (typeof c.toMillis === 'function') return c.toMillis();
  if (typeof c.seconds === 'number') return c.seconds * 1000;
  if (c instanceof Date) return c.getTime();
  return 0;
}

function ymdToDate(ymd) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function startOfWeek(date) {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
}

export function describeEntry(entry, timezone) {
  if (!entry) return '';

  const startDate = toJsDate(entry.startDate);
  const endDate = toJsDate(entry.endDate);
  const startYMD = ymdInTimezone(startDate, timezone);
  const endYMD = endDate ? ymdInTimezone(endDate, timezone) : null;

  const statusWord = entry.status === 'unavailable' ? 'Unavailable' : 'Available';

  let pattern;
  if (entry.type === 'one-off') {
    pattern = endYMD && endYMD !== startYMD
      ? `${startYMD} → ${endYMD}`
      : `${startYMD}`;
  } else if (entry.type === 'weekly') {
    pattern = `${dayLabels(entry.daysOfWeek)} every week, from ${startYMD}`;
    if (endYMD) pattern += ` until ${endYMD}`;
  } else if (entry.type === 'every-n-weeks') {
    pattern = `${dayLabels(entry.daysOfWeek)} every ${entry.weekInterval} weeks, from ${startYMD}`;
    if (endYMD) pattern += ` until ${endYMD}`;
  } else {
    pattern = `(${entry.type})`;
  }

  const reasonSuffix = entry.reason ? ` — ${entry.reason}` : '';
  return `${statusWord}: ${pattern}${reasonSuffix}`;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function dayLabels(daysOfWeek) {
  if (!Array.isArray(daysOfWeek) || daysOfWeek.length === 0) return '(no days)';
  return daysOfWeek.map((d) => DAY_NAMES[d] ?? '?').join(', ');
}
